import React from 'react';
import { GIGA_MARK } from '../IntegrationLogo';
import { EXPORT_META } from './exportUi';

/**
 * BRAND MARKS FOR VIDEO.
 *
 * A creator needs a mark far more often than a screen: a corner bug on the
 * clip, a logo beside a tool's logo when saying "Giga reads your Salesforce",
 * a thumbnail. The screen library had lockups (mark + word in a capsule) but
 * never the bare marks, so anyone wanting just the Giga glyph was cropping it
 * out of a screenshot.
 *
 * These export on a genuinely transparent artboard at 4× — a mark is placed
 * small and scaled by whoever uses it, so it needs the headroom the wide
 * frames do not.
 */

export const GIGA_ORANGE = '#EA580C';

/** Giga's own marks, in the three forms a video actually asks for. */
export type BrandAsset = {
  id: string;
  name: string;
  note: string;
  /** Rendered size on the artboard, before the 4× export. */
  boxW: number;
  boxH: number;
  /** Marks on a dark plate need the checkerboard behind them inverted. */
  onDark?: boolean;
  render: () => React.ReactNode;
};
function Wordmark({
  tone
}: {
  tone: 'ink' | 'white';
}) {
  return <span className="font-logo inline-flex items-center gap-2.5 text-[38px] leading-none tracking-[-0.02em]" style={{
    color: tone === 'white' ? '#FFFFFF' : '#2B211A'
  }} data-id="element-3225" data-mp="1pg6xnqgttzxb">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[34px] object-contain" data-id="element-3226" data-mp="1gh20ookwyen9" />
      Giga
    </span>;
}
export const BRAND_ASSETS: BrandAsset[] = [{
  id: 'brand-mark',
  name: 'Giga mark',
  note: 'Glyph only, transparent',
  boxW: 120,
  boxH: 120,
  render: () => <img src={GIGA_MARK} alt="Giga" className="size-[92px] object-contain" data-id="element-3227" data-mp="28a2hu88sbkbc" />
}, {
  id: 'brand-wordmark',
  name: 'Wordmark',
  note: 'Mark and name, for light backgrounds',
  boxW: 260,
  boxH: 110,
  render: () => <Wordmark tone="ink" data-id="element-3228" data-mp="2vx274pesn7xy" />
}, {
  id: 'brand-wordmark-white',
  name: 'Wordmark, reversed',
  note: 'For dark footage and overlays',
  boxW: 260,
  boxH: 110,
  onDark: true,
  render: () => <Wordmark tone="white" data-id="element-3229" data-mp="p91ub0a4ynf6" />
}, {
  id: 'brand-bug',
  name: 'Corner bug',
  note: 'Mark on an ink chip — sits on any footage',
  boxW: 180,
  boxH: 110,
  render: () => <span className="inline-flex items-center gap-2 rounded-full bg-[#2B211A] px-3.5 py-2" data-id="element-3230" data-mp="u7ljranxuhca">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[18px] object-contain" data-id="element-3231" data-mp="fipcvgxmbad9" />
      <span className="font-logo text-[17px] leading-none text-white" data-id="element-3232" data-mp="kh0jzjwj36rs">Giga</span>
    </span>
}];

/**
 * The closer's ground — the warm near-black the rendered MP4s resolve on.
 * Sampled from the closer's final frame so the still card and the video are
 * literally the same colour; anything close-but-different reads as a mistake
 * when they are cut back to back.
 */
export const CLOSER_GROUND = '#3A322C';

/**
 * THE END CARD — the closer's last frame, frozen.
 *
 * Deliberately identical to where the rendered MP4 lands: the address in the
 * rounded wordmark face, white, centred, on the warm near-black ground, and
 * nothing else. An earlier version stacked a mark, the name and a strapline
 * with the address in a pill — a nicer composition, and wrong, because a clip
 * that ends on the video and one that ends on this card would have looked like
 * they came from two different companies.
 *
 * It carries type — the exception to the rule that these plates stay
 * captionless — because an end card IS the caption, and the words never
 * change.
 */
export function EndCard({
  ratio = '9:16'
}: {
  ratio?: '9:16' | '16:9';
}) {
  return <div className="flex h-full w-full items-center justify-center" style={{
    backgroundColor: CLOSER_GROUND
  }} data-id="element-3233" data-mp="3v4ffbff5tt7v">
      <p className="font-logo leading-none tracking-[-0.01em] text-white" style={{
      fontSize: ratio === '9:16' ? 46 : 64
    }} data-id="element-3234" data-mp="czc5jhe90t14">
        getgiga.com
      </p>
    </div>;
}

/**
 * A LOWER THIRD.
 *
 * The strip that names who is talking. Creators rebuild this by hand in every
 * editor; handing over the branded version means every clip from the team
 * looks like it came from the same company.
 *
 * Exported on a transparent artboard so it drops straight over footage.
 */
export function LowerThird() {
  return <div className="inline-flex items-center gap-3.5 rounded-2xl bg-[#2B211A] px-5 py-4 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.5)]" data-id="element-3235" data-mp="2g6b7nqeo2eby">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[34px] flex-none object-contain" data-id="element-3236" data-mp="2l4cpy949q9md" />
      <span className="min-w-0" data-id="element-3237" data-mp="2q3fttq8ii9sr">
        <span className="block text-[19px] font-bold leading-tight text-white" data-id="element-3238" data-mp="1sia4kx09bq3x">Sarah Chen</span>
        <span className={`${EXPORT_META} block text-[#C9BCAF]`} data-id="element-3239" data-mp="1xglawxrcfihe">Head of Revenue Operations</span>
      </span>
      <span className="ml-2 rounded-full px-3 py-1.5 text-[13px] font-bold text-white" style={{
      backgroundColor: GIGA_ORANGE
    }} data-id="element-3240" data-mp="22fejqcvoqlb8">
        getgiga.com
      </span>
    </div>;
}